import { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Swiper, SwiperSlide } from 'swiper/react';
import SwiperCore, { Navigation, Mousewheel } from 'swiper/core';
import { use100vh } from 'react-div-100vh';

import { bugsnag } from 'shared/utils/services/bugsnag';
import { DEFAULT_ASSET_WIDTH_BEFORE_UI_RENDERS } from 'homepage/enums/contentConstants';
import { GiftCardSpace } from 'gift-cards/components/pdp/GiftCardSpace';
import { setMultipleLayoutOptions } from 'store/slice/pageLayoutSlice';
import { useGiftCardsState } from 'gift-cards/hooks/useGiftCards';
import { useIsMounted } from 'shared/hooks/useIsMounted';
import { useStoreDispatch } from 'store/hooks/storeHooks';
import { useWindowSize } from 'shared/hooks/useWindowSize';

SwiperCore.use([Navigation, Mousewheel]);

interface IGiftCardsCarouselMobileProps {
  selectedIndex?: number;
  setSelectedIndex: (val: number) => void;
  initialSlide?: number;
}

export const GiftCardsCarouselMobile = ({
  initialSlide = 0,
  setSelectedIndex,
}: IGiftCardsCarouselMobileProps) => {
  const { spaces, spaceInMobileEditMode, selectedItemId } = useGiftCardsState();
  const storeDispatch = useStoreDispatch();
  const { isMobile, windowSize } = useWindowSize();
  const isMounted = useIsMounted();
  const windowHeight = use100vh();

  const [canvasWidth, setCanvasWidth] = useState(DEFAULT_ASSET_WIDTH_BEFORE_UI_RENDERS);
  const [swiper, setSwiper] = useState<SwiperCore>(null);

  const maxHeight: number = 0.6 * windowHeight < 770 ? 0.6 * windowHeight : 750;

  const giftCards = Object.values(spaces);

  const handleSlideChange = (swipercore) => {
    const { activeIndex: idx } = swipercore;
    setSelectedIndex(idx);
  };

  useEffect(() => {
    if (isMounted()) {
      if (spaceInMobileEditMode) {
        storeDispatch(setMultipleLayoutOptions({ isFooterVisible: false, isHeaderVisible: false }));
      } else {
        storeDispatch(setMultipleLayoutOptions({ isFooterVisible: true, isHeaderVisible: true }));
      }
      return () => {
        storeDispatch(setMultipleLayoutOptions({ isFooterVisible: true, isHeaderVisible: true }));
      };
    }
  }, [spaceInMobileEditMode]);

  useEffect(() => {
    if (isMounted()) {
      setCanvasWidth(windowSize?.width);
    }
  }, [isMobile, windowSize]);

  useEffect(() => {
    const handleResize = () => {
      if (isMounted() && !spaceInMobileEditMode && swiper) {
        try {
          swiper?.update();
          swiper?.updateSize();
        } catch (error) {
          bugsnag(error, { context: 'Error resizing swiper of spaces' });
        }
      }
    };

    document.addEventListener('resize', handleResize);
    handleResize();
    return () => {
      document.removeEventListener('resize', handleResize);
    };
  });

  return (
    <GiftCardsCarouselMobile.Wrapper $spaceInMobileEditMode={spaceInMobileEditMode}>
      {!spaceInMobileEditMode ? (
        <GiftCardsCarouselMobile.Carousel>
          <GiftCardsCarouselMobile.Swiper
            height={maxHeight + 41.5}
            centeredSlides
            initialSlide={initialSlide}
            onSlideChange={handleSlideChange}
            slidesPerView="auto"
            onSwiper={setSwiper}
            spaceBetween={24}
            observer
          >
            {giftCards?.map((space) => {
              return (
                <SwiperSlide key={`slide-${space.id}`}>
                  {({ isActive }) => (
                    <GiftCardSpace
                      isActive={isActive}
                      space={space}
                      height={maxHeight < 220 ? 220 : maxHeight}
                      hasMobileEditMode
                      selectedObjectToFront
                    />
                  )}
                </SwiperSlide>
              );
            })}
          </GiftCardsCarouselMobile.Swiper>
        </GiftCardsCarouselMobile.Carousel>
      ) : (
        <GiftCardsCarouselMobile.EditMode>
          <GiftCardSpace
            isActive
            space={spaces[spaceInMobileEditMode]}
            height={(canvasWidth - 48) * 1.4964}
            defaultActiveObject={selectedItemId}
            hasMobileEditMode
            selectedObjectToFront
          />
        </GiftCardsCarouselMobile.EditMode>
      )}
    </GiftCardsCarouselMobile.Wrapper>
  );
};

GiftCardsCarouselMobile.Wrapper = styled.div<{ $spaceInMobileEditMode: string }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;

  animation: fadeIn 1s;
  @keyframes fadeIn {
    0% {
      opacity: 0;
    }
    100% {
      opacity: 1;
    }
  }
`;

GiftCardsCarouselMobile.Carousel = styled.div`
  width: 100vw;
  height: 100%;
  max-width: 100vw;
  max-height: 100vh;
  min-height: 0;
  min-width: 0;
  overflow: hidden;

  display: flex;
  flex-direction: column;
  justify-content: center;
`;

GiftCardsCarouselMobile.Swiper = styled(Swiper)<{ height: number }>`
  width: calc(100vw);
  justify-content: center;
  align-items: center;

  animation: fade 1s ease-in-out;

  @keyframes fade {
    0% {
      opacity: 0;
    }
    50% {
      opacity: 0;
    }
    100% {
      opacity: 1;
    }
  }

  .swiper-wrapper {
    margin-top: -12px;
    height: ${({ height }) => `${height}px;`};
    transition-duration: 350ms !important;
    display: flex;
    align-items: flex-end;
  }

  .swiper-slide {
    height: auto;
    width: auto !important;
  }
`;

GiftCardsCarouselMobile.EditMode = styled.div`
  height: calc(100vh - 75px);
  display: flex;
  align-items: center;
  justify-content: center;
`;
